import { Admin, CustomRoutes } from 'react-admin';
import { Navigate, Route } from 'react-router-dom';
import { ApplicationDetailPage } from '@/features/applications/ApplicationDetailPage';
import { ApplicationsPage } from '@/features/applications/ApplicationsPage';
import { AuditPage } from '@/features/audit/AuditPage';
import { CredentialDetailPage } from '@/features/credentials/CredentialDetailPage';
import { CredentialsPage } from '@/features/credentials/CredentialsPage';
import { GatewayAccountDetailPage } from '@/features/gateway-accounts/GatewayAccountDetailPage';
import { GatewayAccountsPage } from '@/features/gateway-accounts/GatewayAccountsPage';
import { OperationDetailPage } from '@/features/operations/OperationDetailPage';
import { OperationsPage } from '@/features/operations/OperationsPage';
import { StatisticsPage } from '@/features/statistics/StatisticsPage';
import { TestBenchPage } from '@/features/test-bench/TestBenchPage';
import { TenantDetailPage } from '@/features/tenants/TenantDetailPage';
import { TenantsPage } from '@/features/tenants/TenantsPage';
import { AccessGatePage } from '@/components/shell/AccessGatePage';
import { i18nProvider } from '@/i18n';
import { CustomLayout } from '@/layout/CustomLayout';
import { authProvider } from '@/providers/authProvider';
import { dataProvider } from '@/providers/dataProvider';
import { theme } from '@/theme/theme';

/**
 * Application shell and routing. The operations/aggregates/detail screens
 * (B2), the tenants screens (B3) and the applications/credentials/gateway
 * accounts screens (B4) all call the typed `payApiClient` directly rather
 * than through a react-admin `<Resource>`, since neither the read-only
 * reports surface nor the administration surface (its writes are their own
 * explicit contract operations, not a generic CRUD shape) map to
 * react-admin's `dataProvider`; the audit console (B5) still adds its
 * screen behind its own placeholder route. `loginPage` is
 * {@link AccessGatePage}, never a local login form (plan D-P still holds: no
 * field ever collects a credential here) — it is the designed landing state
 * react-admin's own `requireAuth` shows whenever `checkAuth` finds no
 * principal, and it renders either the "no session" or the "authenticated
 * but not entitled" screen depending on what the active access provider
 * reports (see `src/access/localAccessProvider.ts` and
 * `src/access/federatedAccessProvider.ts`).
 */
export function App() {
  return (
    <Admin
      layout={CustomLayout}
      theme={theme}
      authProvider={authProvider}
      dataProvider={dataProvider}
      i18nProvider={i18nProvider}
      loginPage={AccessGatePage}
      requireAuth
      disableTelemetry
    >
      <CustomRoutes>
        <Route path="/" element={<Navigate to="/operations" replace />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/operations/:id" element={<OperationDetailPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/tenants/:code" element={<TenantDetailPage />} />
        <Route path="/applications" element={<ApplicationsPage />} />
        <Route path="/applications/:code" element={<ApplicationDetailPage />} />
        <Route path="/credentials" element={<CredentialsPage />} />
        <Route path="/credentials/:id" element={<CredentialDetailPage />} />
        <Route path="/gateway-accounts" element={<GatewayAccountsPage />} />
        <Route path="/gateway-accounts/:id" element={<GatewayAccountDetailPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/test-bench" element={<TestBenchPage />} />
      </CustomRoutes>
    </Admin>
  );
}
